// router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import LoginPage from '../components/pages/LoginPage.vue';
import HomePage from '../components/pages/HomePage.vue';
import BirdsEyeDetailPage from '../components/pages/BirdsEyeDetailPage.vue';
import LegacyDetailPage from '../components/pages/LegacyDetailPage.vue';
import { isAuthenticated } from '@/services/authService';
import ConfigurationPage from '@/components/pages/ConfigurationPage.vue';
import RetailerPage from '@/components/pages/RetailerPage.vue';
import LabPage from '@/components/pages/LabPage.vue';
import SimulatorPage from '@/components/pages/SimulatorPage.vue';
import PvEstimatorPage from '@/components/pages/PvEstimatorPage.vue';
import BatterySizeEstimator from '@/components/pages/BatterySizeEstimator.vue';
import DataExtractorPage from '@/components/pages/DataExtractorPage.vue';
import PerformanceReportPage from '@/components/pages/PerformanceReportPage.vue';
import AiOnboardingEvaluatorPage from '@/components/pages/AiOnboardingEvaluatorPage.vue';
import KnowledgeAssistantPage from '@/components/pages/KnowledgeAssistantPage.vue';
import AdvancedCapabilitiesPage from '@/components/pages/AdvancedCapabilitiesPage.vue';
import ApiTesterPage from '@/components/pages/ApiTesterPage.vue';
import { getUserInfo } from '@/services/cookieService';
import { path } from 'd3-path';

const defaultTitle = 'Renox AI Portal';

const routes = [
  {
    path: '/',
    name: 'LoginPage',
    component: LoginPage,
    meta: {
      title: `${defaultTitle} - Login`,
      showHeader: false,
      showFooter: false,
      requiresAuth: false,
    },
  },
  {
    path: '/home',
    name: 'HomePage',
    component: HomePage,
    meta: {
      title: `${defaultTitle} - Home`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/detail',
    name: 'DetailPage',
    component: BirdsEyeDetailPage,
    meta: {
      title: `${defaultTitle} - Visualized Prediction`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/detail-legacy',
    name: 'DetailPageLegacy',
    component: LegacyDetailPage,
    meta: {
      title: `${defaultTitle} - Visualized Prediction (Legacy)`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/system-configuration',
    name: 'ConfigurationPage',
    component: ConfigurationPage,
    meta: {
      title: `${defaultTitle} - System Configuration`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/retailer',
    name: 'RetailerPage',
    component: RetailerPage,
    meta: {
      title: `${defaultTitle} - Realtime Retailer Feed`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/lab',
    name: 'LabPage',
    component: LabPage,
    meta: {
      title: `${defaultTitle} - Experimental Projects`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/lab/simulator',
    name: 'SimulatorPage',
    component: SimulatorPage,
    meta: {
      title: `${defaultTitle} - Renox Sales Simulator`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/lab/pv-generation-estimator',
    name: 'PV Estimator',    
    component: PvEstimatorPage,
    meta: {
      title: `${defaultTitle} - PV Estimator`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  }, 
  {
    path: '/lab/battery-size-estimator',
    name: 'Battery Size Estimator',    
    component: BatterySizeEstimator,
    meta: {
      title: `${defaultTitle} - Battery Size Estimator`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/lab/data-extractor',
    name: 'Data Extractor',    
    component: DataExtractorPage,
    meta: {
      title: `${defaultTitle} - Data Extractor`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    }, 
  },
  {
    path: '/lab/ai-onboarding-evaluator',
    name: 'AiOnboardingEvaluatorPage',
    component: AiOnboardingEvaluatorPage,
    meta: {
      title: `${defaultTitle} - AI Onboarding Evaluator`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/lab/knowledge-assistant',
    name: 'KnowledgeAssistantPage',
    component: KnowledgeAssistantPage,
    meta: {
      title: `${defaultTitle} - Renox Assistant`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
    },
  },
  {
    path: '/advanced-capabilities',
    name: 'AdvancedCapabilitiesPage',
    component: AdvancedCapabilitiesPage,
    meta: {
      title: `${defaultTitle} - Advanced Capabilities`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: '/dev/api-tester',
    name: 'ApiTesterPage',
    component: ApiTesterPage,
    meta: {
      title: `${defaultTitle} - API Tester`,
      showHeader: true,
      showFooter: false,
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
    {
      path: '/performance-report',
      name: 'Performance Report',    
      component: PerformanceReportPage,
      meta: {
        title: `${defaultTitle} - Performance Report`,
        showHeader: true,
        showFooter: false,
        requiresAuth: true,
      },
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Global navigation guard
router.beforeEach((to, from, next) => {
  const isAuth = isAuthenticated();
  const cookieData = getUserInfo();
  const isAdmin = cookieData && Number(cookieData.accessLevel) === 10;

  if (to.meta.requiresAuth && !isAuth) {
    // If the route requires auth and the user is not authenticated, redirect to login
    next({ name: 'LoginPage' });
  } else if (to.meta.requiresAdmin && !isAdmin) {
    next({ name: 'HomePage' });
  } else if (to.name === 'LoginPage' && isAuth) {
    // If the user is authenticated and tries to access the login page, redirect to another page
    next({ name: 'HomePage' }); // Redirect to a default logged-in page
  } else {
    next(); // Proceed to the route
  }
});

router.beforeEach((to, from, next) => {
  if (to.meta.title) {
    document.title = to.meta.title;
  } else {
    document.title = defaultTitle; // fallback title
  }
  next();
});

export default router;
